"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { GuideStartLink } from "./GuideStartLinks";

type GuideAuthUser = {
  id: string;
  username: string;
  role?: "admin" | "user";
  remainingChats?: number;
  membershipPlan?: "free" | "monthly" | "yearly" | "lifetime";
  monthlyQuota?: number;
};

type GuideAuthStatus = {
  authenticated: boolean;
  setupRequired: boolean;
  user: GuideAuthUser | null;
};

const PLAN_LABELS: Record<NonNullable<GuideAuthUser["membershipPlan"]>, string> = {
  free: "免费",
  monthly: "月卡",
  yearly: "年卡",
  lifetime: "永久卡",
};

export function GuideUserStatus() {
  const [status, setStatus] = useState<GuideAuthStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/app-auth/status", { cache: "no-store" })
      .then((res) => res.ok ? res.json() as Promise<GuideAuthStatus> : null)
      .then((data) => {
        if (!cancelled) setStatus(data);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!status?.authenticated || !status.user) {
    return <GuideStartLink />;
  }

  const user = status.user;
  const planLabel = PLAN_LABELS[user.membershipPlan ?? "free"] ?? "会员";
  const quotaLabel = user.remainingChats !== undefined ? `${user.remainingChats}/${user.monthlyQuota ?? "-"}` : null;

  return (
    <div className="guide-user-status">
      <Link
        className="guide-user-pill"
        href="/chat"
        title={quotaLabel ? `${user.username} | ${planLabel} | 剩余 ${quotaLabel} 次` : `${user.username} | ${planLabel}`}
      >
        <span className="guide-user-name">{user.username}</span>
        <span className="guide-user-plan">{planLabel}</span>
        {quotaLabel && <span className="guide-user-quota">剩余 {quotaLabel}</span>}
      </Link>
      {user.role === "admin" && (
        <Link className="guide-admin-link" href="/admin">
          管理
        </Link>
      )}
    </div>
  );
}
